import React, { useState } from 'react'
import Highcharts from "highcharts";
import more from "highcharts/highcharts-more";
import HighchartsReact from "highcharts-react-official";
import { Box, Card, Grid, IconButton, Typography } from '@mui/material';
import { BsThreeDotsVertical } from 'react-icons/bs';
import TitleBar from 'components/TitleBar';
import { costKpiData } from './mapper';
import { enterpriseTokens, overviewChartPalette } from 'styles/theme';

const CostOverview = ({ costKpi, isLoading, error }) => {
    const [speed] = useState(1);
    const progress = 0;
    const cpi = parseFloat(costKpi?.data?.[0]?.cpi);
    const clampedValue = Number.isFinite(cpi) ? Math.max(0, Math.min(2, cpi)) : 0;
    const kpiData = costKpiData(costKpi?.data?.[0])
    const chartOptions = {
        chart: {
            type: "gauge",
            plotBackgroundColor: null,
            plotBackgroundImage: null,
            plotBorderWidth: 0,
            plotShadow: false,
            height: "47%",
            backgroundColor: "transparent",
        },
        title: {
            text: "",
        },
        pane: {
            startAngle: -90,
            endAngle: 89.9,
            background: null,
            center: ["50%", "95%"],
            size: "180%",
        },
        yAxis: {
            min: 0,
            max: 2,
            tickPixelInterval: 72,
            tickPosition: "inside",
            tickColor: Highcharts.defaultOptions.chart.backgroundColor || "#FFFFFF",
            tickLength: 20,
            tickWidth: 0,
            minorTickInterval: null,
            labels: {
                enabled: false,
                // distance: 20,
                style: {
                    fontSize: "12px",
                },
            },
            lineWidth: 0,
            plotBands: [
                {
                    from: 0,
                    to: 0.5,
                    color: overviewChartPalette.coral,
                    thickness: 28,
                    borderRadius: '2px'
                },
                {
                    from: 0.5,
                    to: 1,
                    color: overviewChartPalette.amber,
                    thickness: 28,
                    borderRadius: '2px'
                },
                {
                    from: 1,
                    to: 1.5,
                    color: overviewChartPalette.teal,
                    thickness: 28,
                    borderRadius: '2px'
                },
                {
                    from: 1.5,
                    to: 2,
                    color: overviewChartPalette.indigo,
                    thickness: 28,
                    borderRadius: '2px'
                }
            ]

        },
        credits: {
            enabled: false,
        },
        series: [
            {
                name: "Cost Efficiency (EV/AC)",
                data: [clampedValue],
                tooltip: {
                    valueSuffix: "",
                    pointFormat: '<span style="color:{point.color}">●</span> {series.name}: <b>{point.y:.2f}</b>'
                },
                dataLabels: {
                    enabled: true,
                    formatter: function () {
                        return `<div>${cpi.toFixed(2)}</div>`;
                    },
                    borderWidth: 0,
                    y: -60,
                    // x:20,
                    style: {
                        fontSize: "26px",
                        color: overviewChartPalette.navy,
                    },
                },
                dial: {
                    radius: "80%",
                    backgroundColor: overviewChartPalette.navy,
                    baseWidth: 7,
                    baseLength: "0%",
                    rearLength: "0%",
                },
                pivot: {
                    backgroundColor: overviewChartPalette.navy,
                    radius: 5,
                },
            },
        ],
    };
    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: "3px",
                border: `1px solid ${enterpriseTokens.lineSoft}`,
                p: 0,
                height: '100%',
                pb: 2,
                boxSizing: "border-box",
                backgroundColor: "#FFFFFF",
            }}
        >
            <Grid container spacing={0} >
                <TitleBar title={"Cost Overview"} description={" Track your spending, budget and cost trends for effective financial control."} />
            </Grid>
            {!isLoading && !error && kpiData && <Grid container spacing={1} sx={{ pt: 0, alignItems: "center" }}>
                <Grid size={{ md: 4 }} sx={{ p: 2, pb: 0 }}>
                    <Box sx={{ display: "grid", gap: 1 }}>
                        {kpiData?.map((item) => (
                            <Box
                                key={item.key}
                                sx={{
                                    p: 1.5,
                                    borderRadius: "3px",
                                    border: `1px solid ${enterpriseTokens.lineSoft}`,
                                    backgroundColor: "#F8FAFC",
                                    borderLeft: `4px solid ${item?.color}`,
                                }}
                            >
                                <Typography sx={{
                                    fontFamily: "Inter, Outfit, sans-serif",
                                    fontWeight: 800,
                                    fontSize: '11px',
                                    letterSpacing: "0px",
                                    color: enterpriseTokens.muted,
                                    whiteSpace: "nowrap",
                                    textTransform: "uppercase",
                                }}>{item.label}</Typography>
                                <Typography sx={{
                                    fontFamily: "Inter, Outfit, sans-serif",
                                    fontWeight: 850,
                                    fontSize: '24px',
                                    letterSpacing: "0px",
                                    color: enterpriseTokens.navy,
                                }}>INR {item.value}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 8 }} sx={{ pt: 0 }}>
                    <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                    <Typography sx={{
                        fontFamily: "Inter, Outfit, sans-serif",
                        fontWeight: 800,
                        fontSize: '12px',
                        letterSpacing: "0px",
                        color: enterpriseTokens.muted,
                        textAlign: "center"
                    }}>Cost Efficiency (EV/AC)</Typography>
                </Grid>
            </Grid>}
            {isLoading && (
                <Grid item sx={{ flex: "0 0 auto", display: 'flex', alignItems: 'center', px: 3 }}>
                    <Box>Loading Data...</Box>
                </Grid>
            )}
            {error && (
                <Grid item xs={12} sx={{ height: "100%", p: 2 }}>
                    <Box>
                        Error loading data: {error?.status} - {error?.data?.message || "Please try again."}
                    </Box>
                </Grid>
            )}
        </Card>
    )
}

export default CostOverview
